import z from 'zod';

const dimensionUnit = z.enum(['in', 'cm', 'mm', 'ft', 'm']);
const weightUnit = z.enum(['lb', 'kg']);
const crateStatus = z.enum([
  'DRAFT',
  'PACKED',
  'LOADED',
  'SHIPPED',
  'DELIVERED',
]);

// DRF DecimalField serializes as a string; allow string|number|null.
const decimalLike = z.union([z.string(), z.number()]).nullable();
const emptyToUndefined = (value: unknown) =>
  value === '' || value === null ? undefined : value;
const optionalText = z.preprocess(
  emptyToUndefined,
  z.string().trim().optional().nullable(),
);
const optionalPositiveNumber = z.preprocess(
  emptyToUndefined,
  z.coerce.number().positive('Must be greater than 0').optional().nullable(),
);
const optionalRelease = z.preprocess(
  emptyToUndefined,
  z.coerce.number().positive('Release must be valid').optional().nullable(),
);

const crateBase = {
  id: z.number(),
  project: z.number(),
  release: z.number().nullable(),
  crate_number: z.string().nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  length: decimalLike,
  width: decimalLike,
  height: decimalLike,
  dimension_unit: dimensionUnit.nullable(),
  weight: decimalLike,
  weight_unit: weightUnit.nullable(),
  status: crateStatus,
  item_count: z.number(),
  created_at: z.string(),
};

const crateObject = z.object(crateBase);

export const crateItem = z.object({
  id: z.number(),
  item_type: z.enum(['FRAME', 'PART', 'WORK_ORDER', 'MISC']),
  frame: z.number().nullable(),
  frame_name: z.string().nullable().optional(),
  release_number: z.string().nullable().optional(),
  frame_description: z.string().nullable().optional(),
  frame_attributes: z.record(z.string(), z.unknown()).optional(),
  part: z.number().nullable(),
  part_number: z.string().nullable().optional(),
  part_description: z.string().nullable().optional(),
  part_attributes: z.record(z.string(), z.unknown()).optional(),
  work_order: z.number().nullable(),
  work_order_identifier: z.string().nullable().optional(),
  quantity: z.number(),
  notes: z.string().nullable(),
  source_step: z.string().nullable(),
});

const crateWithItems = z.object({
  id: z.number(),
  project: z.number(),
  project_name: z.string().nullable().optional(),
  release: z.number().nullable(),
  release_number: z.string().nullable().optional(),
  crate_number: z.string().nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  length: decimalLike,
  width: decimalLike,
  height: decimalLike,
  dimension_unit: dimensionUnit.nullable(),
  weight: decimalLike,
  weight_unit: weightUnit.nullable(),
  status: crateStatus,
  created_at: z.string(),
  items: z.array(crateItem),
});

// ---- Create crate ----
export const postCrate = {
  body: z.object({
    name: optionalText,
    release: optionalRelease,
    description: optionalText,
    length: optionalPositiveNumber,
    width: optionalPositiveNumber,
    height: optionalPositiveNumber,
    dimension_unit: dimensionUnit.optional(),
    weight: optionalPositiveNumber,
    weight_unit: weightUnit.optional().nullable(),
  }),
  response: crateObject,
  params: z.object({ project_id: z.coerce.number() }),
  query: z.never(),
};
const postCrateSchema = z.object(postCrate);
export type PostCrate = z.infer<typeof postCrateSchema>;

// ---- List crates ----
export const getCrates = {
  body: z.never(),
  response: z.array(crateObject),
  params: z.object({ project_id: z.coerce.number() }),
  query: z
    .object({
      release: z.coerce.number().optional(),
      status: crateStatus.optional(),
    })
    .optional(),
};
const getCratesSchema = z.object(getCrates);
export type GetCrates = z.infer<typeof getCratesSchema>;

// ---- Eligible frames for crating ----
export const getCrateEligibleFrames = {
  body: z.never(),
  response: z.array(
    z.object({
      frame_id: z.number(),
      frame_name: z.string(),
      release_id: z.number(),
      release_number: z.string(),
      work_order_id: z.number(),
      work_order_identifier: z.string().nullable(),
      remaining_quantity: z.number(),
    }),
  ),
  params: z.object({ project_id: z.coerce.number() }),
  query: z.object({ release: z.coerce.number().optional() }).optional(),
};
const getCrateEligibleFramesSchema = z.object(getCrateEligibleFrames);
export type GetCrateEligibleFrames = z.infer<
  typeof getCrateEligibleFramesSchema
>;

// ---- Crate detail with items ----
export const getCrate = {
  body: z.never(),
  response: crateWithItems,
  params: z.object({ crate_id: z.coerce.number() }),
  query: z.never(),
};
const getCrateSchema = z.object(getCrate);
export type GetCrate = z.infer<typeof getCrateSchema>;

// ---- Update crate ----
export const patchCrate = {
  body: z.object({
    name: optionalText,
    release: optionalRelease,
    description: optionalText,
    length: optionalPositiveNumber,
    width: optionalPositiveNumber,
    height: optionalPositiveNumber,
    dimension_unit: dimensionUnit.optional(),
    weight: optionalPositiveNumber,
    weight_unit: weightUnit.optional().nullable(),
    status: crateStatus.optional(),
  }),
  response: crateObject,
  params: z.object({ crate_id: z.coerce.number() }),
  query: z.never(),
};
const patchCrateSchema = z.object(patchCrate);
export type PatchCrate = z.infer<typeof patchCrateSchema>;

// ---- Delete crate ----
export const deleteCrate = {
  body: z.never(),
  response: z.never(),
  params: z.object({ crate_id: z.coerce.number() }),
  query: z.never(),
};
const deleteCrateSchema = z.object(deleteCrate);
export type DeleteCrate = z.infer<typeof deleteCrateSchema>;

// ---- Add items (frames/parts) ----
export const postCrateItems = {
  body: z.object({
    frames: z
      .array(
        z.object({
          frame_id: z.coerce.number(),
          quantity: z.coerce.number().min(1),
          notes: z.string().optional().nullable(),
        }),
      )
      .optional(),
    parts: z
      .array(
        z.object({
          part_id: z.coerce.number(),
          quantity: z.coerce.number().min(1),
          notes: z.string().optional().nullable(),
        }),
      )
      .optional(),
  }),
  response: crateWithItems,
  params: z.object({ crate_id: z.coerce.number() }),
  query: z.never(),
};
const postCrateItemsSchema = z.object(postCrateItems);
export type PostCrateItems = z.infer<typeof postCrateItemsSchema>;

// ---- Remove items ----
export const deleteCrateItems = {
  body: z.object({
    item_ids: z.array(z.coerce.number()).min(1),
  }),
  response: z.object({
    removed: z.array(z.number()),
    errors: z.array(z.object({ detail: z.string() })).optional(),
  }),
  params: z.object({ crate_id: z.coerce.number() }),
  query: z.never(),
};
const deleteCrateItemsSchema = z.object(deleteCrateItems);
export type DeleteCrateItems = z.infer<typeof deleteCrateItemsSchema>;

// ---- Export crate sheet (blob) ----
export const exportCrateSheet = {
  body: z.object({}).optional(),
  response: z.any(),
  params: z.object({ crate_id: z.coerce.number() }),
  query: z.never(),
};
const exportCrateSheetSchema = z.object(exportCrateSheet);
export type ExportCrateSheet = z.infer<typeof exportCrateSheetSchema>;

// ---- Export packing list (blob) ----
export const exportPackingList = {
  body: z.never(),
  response: z.any(),
  params: z.object({ project_id: z.coerce.number() }),
  query: z.object({ release: z.coerce.number().optional() }).optional(),
};
const exportPackingListSchema = z.object(exportPackingList);
export type ExportPackingList = z.infer<typeof exportPackingListSchema>;
